import type { Session, SessionMessageResponse } from "@/lib/session";
import type { Agent } from "@/hooks/use-agents";
import type { AccessibleProjectSummary, Project, Task } from "@/lib/project-types";
import type { ConversationViewSummary } from "@/api/conversation-views";
import type { UnifiedPreferencesSnapshot } from "@/lib/unified-preferences";
import { estimateCacheBytes } from "@/lib/ui-cache";

const str = (value: unknown): value is string => typeof value === "string";
const safeId = (value: unknown): value is string => str(value) && value.length > 0 && value.length <= 256;
const secretPattern = /(?:https?:\/\/[^\s]+(?:[?&](?:token|signature|X-Amz-)|\/download\/)|-----BEGIN [A-Z ]+PRIVATE KEY-----|\bBearer\s+[A-Za-z0-9._~-]{16,}|\bsk-[A-Za-z0-9_-]{12,}|\bAKIA[0-9A-Z]{16}\b|\b(?:api[_-]?key|access[_-]?token|refresh[_-]?token|client[_-]?secret)\s*[:=]\s*\S{8,}|\beyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,})/i;
const safeText = (value: unknown): value is string => str(value) && value.length <= 4096 && !secretPattern.test(value);
const ids = (value: unknown): string[] => Array.isArray(value) ? value.filter(safeId).slice(0, 100) : [];

export function sessionPreview(session: Session): Session | null {
  if (!safeId(session.id) || !safeText(session.title)) return null;
  return {
    id: session.id, title: session.title, tenant_id: "", user_id: "", platform: "",
    external_workspace_id: "", external_channel_id: "", external_thread_id: null,
    created_at: str(session.created_at) ? session.created_at : "",
    last_active_at: str(session.last_active_at) ? session.last_active_at : "",
    is_archived: session.is_archived === true, metadata: {},
    agent_id: safeId(session.agent_id) ? session.agent_id : null,
    parent_session_id: safeId(session.parent_session_id) ? session.parent_session_id : null,
    session_type: session.session_type === "thread" ? "thread" : null,
    has_visible_children: session.has_visible_children === true,
    project_id: safeId(session.project_id) ? session.project_id : null,
    is_pinned: session.is_pinned === true,
    pin_source: null,
    can_read: false, can_participate: false, can_manage: false,
  };
}

export function agentPreview(agent: Agent): Agent | null {
  if (!safeId(agent.id) || !safeText(agent.name)) return null;
  return {
    id: agent.id, name: agent.name, type: agent.type === "team" ? "team" : "agent",
    description: safeText(agent.description) ? agent.description : undefined,
    tier: agent.tier === "secondary" ? "secondary" : "primary",
    order: Number.isFinite(agent.order) ? agent.order : 100,
    // Config, paths, capabilities and executable workflows are never read from disk.
    views: (Array.isArray(agent.views) ? agent.views : []).filter((view) => view && safeId(view.id) && safeText(view.title) && safeText(view.icon) && Number.isFinite(view.order)).map((view) => ({
      id: view.id, title: view.title, type: view.type, icon: view.icon, order: view.order,
    })) ?? [],
    actions: [],
  };
}

export type CachedProject = Pick<Project, "id" | "name" | "is_pinned">;
export function projectPreview(project: Project): CachedProject | null {
  if (!safeId(project.id) || !safeText(project.name)) return null;
  return { id: project.id, name: project.name, is_pinned: project.is_pinned === true };
}

export function accessibleProjectPreview(project: AccessibleProjectSummary): AccessibleProjectSummary | null {
  if (!safeId(project.id) || !safeText(project.name)) return null;
  return {
    id: project.id, name: project.name, is_pinned: project.is_pinned === true,
    description_preview: "", created_by: "", created_at: "", last_activity_at: "",
    session_count: 0, open_task_count: 0, visibility: "Private",
  };
}

export function preferencePreview(snapshot: UnifiedPreferencesSnapshot): UnifiedPreferencesSnapshot {
  return {
    agentPins: ids(snapshot.agentPins),
    sidebarPinOrder: { agentIds: ids(snapshot.sidebarPinOrder.agentIds), projectIds: ids(snapshot.sidebarPinOrder.projectIds) },
    preferences: { submit_shortcut: snapshot.preferences.submit_shortcut === "ctrl_enter" ? "ctrl_enter" : "enter" },
    updatedAt: null,
  };
}

export interface CachedSessionDetail { id: string; title: string; parent_session_id: string | null }
export function sessionDetailPreview(value: Session): CachedSessionDetail | null {
  if (!safeId(value.id) || !safeText(value.title)) return null;
  return { id: value.id, title: value.title, parent_session_id: safeId(value.parent_session_id) ? value.parent_session_id : null };
}
export interface CachedProjectDetail { id: string; name: string }
export function projectDetailPreview(value: Project): CachedProjectDetail | null {
  if (!safeId(value.id) || !safeText(value.name)) return null;
  return { id: value.id, name: value.name };
}
export interface CachedViewLabel { id: string; agent_id: string; title: string; icon: string; type: string; order: number }
export function viewLabelPreview(value: ConversationViewSummary): CachedViewLabel | null {
  if (!safeId(value.id) || !safeId(value.agent_id) || !safeText(value.title) || !safeText(value.icon) || !safeId(value.type) || !Number.isFinite(value.order)) return null;
  return { id: value.id, agent_id: value.agent_id, title: value.title, icon: value.icon, type: value.type, order: value.order };
}

// Only plain text canonical rows with a tiny set of scalar display metadata
// qualify. HTML, links, paths, object URLs and tool/HITL payloads stay in RAM.
const forbidden = /(?:blob:|data:)/i;
export function messagePreview(value: SessionMessageResponse): SessionMessageResponse | null {
  if (!safeId(value.id) || /^(?:optimistic|temp|local|pending)[-:]/i.test(value.id) || !safeId(value.session_id) || !safeId(value.run_id)
    || !["user", "assistant"].includes(value.role)
    || !str(value.content) || forbidden.test(value.content) || secretPattern.test(value.content)
    || !str(value.created_at) || !str(value.status) || /^(?:stream|pending|optimistic)/i.test(value.status)) return null;
  const metadata: Record<string, string | number | boolean> = {};
  for (const field of ["phase", "tool_name", "toolName", "source", "action_label", "task_id", "voiceTranscript", "tokens_before", "summary_length", "flash_mode"] as const) {
    const item = value.metadata?.[field];
    if ((str(item) && safeText(item) && !forbidden.test(item)) || typeof item === "boolean" || (typeof item === "number" && Number.isFinite(item))) metadata[field] = item;
  }
  const row = { id: value.id, session_id: value.session_id, run_id: value.run_id, role: value.role, content: value.content, status: value.status, created_at: value.created_at, metadata };
  return estimateCacheBytes(row) <= 128 * 1024 ? row : null;
}

export function messageTailPreview(rows: SessionMessageResponse[]): SessionMessageResponse[] {
  const selected = rows.slice(-40);
  if (rows.length > 40 && selected.length && rows[rows.length - 41]?.run_id === selected[0].run_id) {
    const splitRun = selected[0].run_id;
    while (selected.length && selected[0].run_id === splitRun) selected.shift();
  }
  const safe = selected.map(messagePreview);
  const incompleteRuns = new Set(selected.filter((_, index) => !safe[index]).map((row) => row.run_id));
  const tail = safe.filter((row): row is SessionMessageResponse => !!row && !incompleteRuns.has(row.run_id));
  while (tail.length && estimateCacheBytes(tail) > 1 * 1024 * 1024) {
    const firstRun = tail[0].run_id;
    while (tail.length && tail[0].run_id === firstRun) tail.shift();
  }
  return tail;
}

export type CachedTask = Pick<Task, "id" | "project_id" | "session_id" | "assigned_to" | "title" | "status" | "created_at" | "updated_at" | "due_date">;
export function taskPreview(task: Task): CachedTask | null {
  if (!safeId(task.id) || !safeId(task.project_id) || !safeText(task.title)) return null;
  return { id: task.id, project_id: task.project_id, session_id: safeId(task.session_id) ? task.session_id : null,
    assigned_to: safeId(task.assigned_to) ? task.assigned_to : null, title: task.title,
    status: task.status, created_at: str(task.created_at) ? task.created_at : "", updated_at: str(task.updated_at) ? task.updated_at : "",
    ...(Object.prototype.hasOwnProperty.call(task, "due_date") ? { due_date: task.due_date } : {}) };
}

export function restoreTaskPreview(value: CachedTask): Task | null {
  if (!safeId(value?.id) || !safeId(value.project_id) || !safeText(value.title) || !["open", "in_progress", "on_hold", "done", "cancelled"].includes(value.status)) return null;
  return { ...value, description: null, artifacts: [], tags: [], created_by: "" };
}
